import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Fade,
  Input,
  Typography,
} from "@mui/material";
import ClockIcon from "@mui/icons-material/LockClock";
import React, { useEffect, useState } from "react";
import CustomButton from "../components/CustomButton";
import { collection, db, getDocs } from "../firebaseConfig";
import dayjs from "dayjs";
import { Person } from "@mui/icons-material";
import { subscribeToNewsletter } from "../utils/brevoSubscribe";
import LoadingScreen from "../components/loading/LoadingScreen";

const News = () => {
  const [articles, setArticles] = useState([]);
  const [activeArticle, setActiveArticle] = useState({});
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch data from Firestore when the component loads
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const querySnapshot = await getDocs(collection(db, "articles"));
      const articleList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setArticles(articleList);
      setActiveArticle(articleList[0]);
      setLoading(false);
    };

    fetchData();
  }, []);

  // Handling subscribe button
  const handleSubscribe = async () => {
    if (!email) return;
    const result = await subscribeToNewsletter(email);

    if (result.success) {
      alert(
        "Thanks for subscribing to our newsletter. Check your inbox for the latest update!"
      );
    } else {
      alert(`Failed to subscribe: ${result.error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await fetch(
      "https://api.hsforms.com/submissions/v3/integration/submit/143406952/0d30c480-2f47-4d19-af7c-7121c18c7aa8",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: [
            {
              name: "email",
              value: email,
            },
          ],
        }),
      }
    );
    const result = await response.json();
    console.log(result);
    if (response.ok) {
      alert(
        "Thanks for subscribing to our newsletter. Check your inbox for the latest update!"
      );
    } else {
      alert(`Failed to subscribe: ${result.message || "Unknown error"}`);
    }
  };

  const convertFirebaseTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "Invalid Date";
    return dayjs.unix(timestamp.seconds).format("MMMM D, YYYY");
  };

  return (
    <>
      {loading ? (
        <Box>
          <LoadingScreen />
        </Box>
      ) : (
        <Box sx={{ width: "100vw" }}>
          {activeArticle?.title && (
            <Fade key={activeArticle?.id} in={true} timeout={700}>
              <Box>
                <Container
                  sx={{
                    height: {md: "50vh"},
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    mt: { xs: 10, md: 0 },
                    mb: { xs: 5, md: 5 },
                  }}
                >
                  <Box sx={{ width: { xs: "100%", md: "45%" } }}>
                    <Typography variant="h2">
                      <span style={{ color: "#DB5430" }}>Latest</span> News
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "primary.main",
                        color: "primary.dark",
                        px: 1,
                        width: "60px",
                        borderRadius: 1,
                        my: 2,
                      }}
                    >
                      <Typography>{activeArticle.category}</Typography>
                    </Box>
                    <Typography variant="h2">{activeArticle.title}</Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        my: 2,
                      }}
                    >
                      <ClockIcon />
                      <Typography
                        variant="body2"
                        color="secondary.light"
                        sx={{ ml: 0.5 }}
                      >
                        {convertFirebaseTimestamp(activeArticle.createdAt)}
                      </Typography>

                      <Person />
                      <Typography
                        variant="body2"
                        color="secondary.light"
                        sx={{ ml: 0.5 }}
                      >
                        {activeArticle.author}
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {activeArticle.subtitle}
                    </Typography>
                  </Box>

                  {/* News Image */}
                  <Box
                    sx={{
                      width: { xs: "100%", md: "50%" },
                      height: { xs: "200px", md: "70%" },
                      backgroundImage: `url(${activeArticle.image})`,
                      backgroundSize: "cover",
                      borderRadius: 5,
                    }}
                  />
                </Container>
                <Container>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {activeArticle.content}
                  </Typography>
                </Container>
              </Box>
            </Fade>
          )}
          {/* More news cards */}
          <Typography
            variant="h2"
            my={2.5}
            textAlign={"center"}
            sx={{ display: { xs: "block", md: "block" }, my: {xs: 5, md: 5} }}
          >
            <span style={{ color: "#DB5430" }}>More</span> News
          </Typography>
          <Container
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
              width: "100%",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            {articles.map((news) => (
              <Card
                key={news.id}
                sx={{
                  width: { xs: "100%", md: "280px" },
                  boxShadow: "none",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setActiveArticle(news);
                }}
              >
                <CardMedia
                  component="img"
                  src={news.image}
                  sx={{ borderRadius: 2, mb: 2, height: "200px" }}
                />
                <CardContent sx={{ p: 0 }}>
                  <Typography variant="h6">{news.title}</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      py: 2,
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 3,
                    }}
                  >
                    {news.subtitle}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {convertFirebaseTimestamp(news.createdAt)}
                    </Typography>
                    <Typography variant="body2" color="primary.main">
                      Read More
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Container>
          {/* Subscribe to newsletter section */}
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "40vh",
              justifyContent: "center",
              alignItems: "center",
              mb: { xs: 5, md: 0 },
              mt: { xs: 5, md: 0 },
            }}
          >
            <Typography variant="h7">Newsletter</Typography>
            <Typography
              variant="h2"
              maxWidth={"250px"}
              my={4}
              textAlign={"center"}
            >
              Subscribe to{" "}
              <span style={{ color: "#DB5430" }}>our newsletter</span>
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                width: { xs: "100%", md: "40%" },
              }}
            >
              <Input
                variant="outlined"
                placeholder="Enter your email address"
                sx={{
                  color: "#222222",
                  width: { xs: "100%", md: "60%" },
                  my: { xs: 2, md: 0 },
                }}
                onChange={(e) => setEmail(e.target.value)}
              />
              <CustomButton onClick={handleSubmit}>
                <Typography mx={5}>Send</Typography>
              </CustomButton>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
};

export default News;
